import "./marketRisk.css";
import { getApiConfig } from "../config";
import { proxyFetch } from "../api/proxyFetch";

export interface MarketRiskPoint {
  date: string;
  value: number;
}

export interface MarketRiskRecession {
  start: string;
  end: string | null;
}

export interface MarketRiskIndicator {
  id: string;
  name: string;
  seriesId: string | null;
  role: string;
  note: string;
  latestDisplay: string;
  latestDate: string | null;
  percentile20y: number | null;
  direction: "up" | "down" | "flat";
  status?: "safe" | "tight" | "watch" | "off";
  correlationText: string | null;
  history: MarketRiskPoint[];
  chart: {
    zeroLine: boolean;
    threshold: number | null;
    thresholdLabel: string | null;
    valueDecimals: number;
  };
}

export interface MarketRiskBlend {
  threshold: number;
  latest: number | null;
  latestMonth: string | null;
  inDanger: boolean;
  futureStart: string;
  futureEnd: string;
  beforeShare: number | null;
  duringShare: number | null;
  otherShare: number | null;
  correlationText: string | null;
  history: MarketRiskPoint[];
  parts: Array<{ id: string; name: string; history: MarketRiskPoint[] }>;
  risk?: { latest: number; history: MarketRiskPoint[] } | null;
}

export interface MarketRiskSnapshot {
  ok: boolean;
  stale?: boolean;
  updatedAt: string;
  asOf: string;
  sentence: string;
  posture: "calm" | "tighten" | "cut" | "unknown";
  drawdown: {
    score: number | null;
    band: "calm" | "tighten" | "cut" | "unknown";
    label: string;
    weekChange: number | null;
    weightsNote: string;
    components: Array<{ id: string; name: string; weight: number; score: number; detail: string }>;
  };
  recession: {
    probabilityPct: number | null;
    spreadPct: number | null;
    spreadMonth: string | null;
    partialMonth: boolean;
    sahm: number | null;
    sahmDate: string | null;
    sahmTriggered: boolean;
  };
  shocks: Array<{
    id: string;
    name: string;
    active: boolean;
    status?: "safe" | "tight" | "watch" | "off";
    reading?: string | null;
    limit?: string | null;
    detail: string;
    history?: MarketRiskPoint[];
  }>;
  indicators: MarketRiskIndicator[];
  blend?: MarketRiskBlend | null;
  recessions: MarketRiskRecession[];
  footnotes: string[];
  margin?: { used: number | null; asOf: string | null };
}

const SHOCK_HELP: Record<string, { what: string; how: string; normal: string; dangerous: string }> = {
  funding: {
    what: "SOFR is the rate for overnight secured borrowing. Interest on reserve balances (IORB) is what the Fed pays banks on cash parked at the Fed. The gap is a live reading of money-market plumbing.",
    how: "The watch uses the latest SOFR and IORB when the two prints are within five days of each other. It opens when SOFR is more than 0.15 percentage points above IORB.",
    normal: "SOFR usually sits at IORB or a few hundredths of a point below it. A small negative gap is ordinary.",
    dangerous: "Above about 0.15 pp, cash is getting scarce in repo. September 2019 and March 2020 went much wider than that. The row names the channel. It does not name the cause.",
  },
  credit: {
    what: "Moody's Baa corporate yield minus the 10-year Treasury. It is the extra yield lenders demand to own ordinary company bonds instead of government bonds.",
    how: "The watch compares the latest spread with the average of the prior three months (about 63 business days). It opens when the spread is more than 0.40 percentage points above that average. The level also feeds the weeks-ahead score: about 3 pp floors the score near the middle, and about 4 pp or more floors it high.",
    normal: "A spread around 1.5–2.5 pp, drifting slowly, is ordinary. Sitting below the three-month average is calm.",
    dangerous: "A jump of 0.40 pp or more in a quarter means lenders are re-pricing risk quickly. Across past recessions this spread averaged about 3.4 pp, and the 2008 spike went toward 6 pp.",
  },
  energy: {
    what: "West Texas Intermediate is the US crude oil benchmark. Large oil spikes have accompanied the recessions of 1973, 1979, and 1990 because energy costs hit households and inflation.",
    how: "The watch compares the latest price with the price about 63 business days earlier. It opens only on a rise of more than 25%. A fall in oil does not open it.",
    normal: "A move of a few percent to the mid-teens over three months is common and leaves the watch closed.",
    dangerous: "A rise above 25% in three months is the tripwire. It is a channel for a growth scare, not a date for a stock-market drop.",
  },
  policy: {
    what: "The 3-month Treasury bill versus the effective federal funds rate. Bills are where Treasury cash-flow stress and debt-ceiling squeezes show up first.",
    how: "The watch uses the latest bill yield minus fed funds when the two prints are within five days. It opens when that gap is wider than 0.40 percentage points in either direction.",
    normal: "Bills usually trade within about 0.10–0.25 pp of the fed-funds rate. A gap inside that band stays closed.",
    dangerous: "Beyond 0.40 pp, the bill market is dislocated from the policy rate. That has shown up around debt-ceiling and Treasury-cash crunches, and when the market is pricing an abrupt policy break.",
  },
  unwind: {
    what: "VIX is the options market's price for S&P 500 volatility over the next 30 days. The 3-month VIX is the same idea over the next three months.",
    how: "The watch subtracts the 3-month VIX from spot VIX, and only when both prints are within a week of each other and the 3-month series is no more than 14 days old. It opens when spot VIX is higher (backwardation). If the 3-month series is stale, the watch stays off.",
    normal: "Spot VIX below the 3-month index is the usual shape. A gap of a few points in that direction is calm.",
    dangerous: "Backwardation means traders are paying more for protection now than for protection further out. That usually means a scramble is already underway, so it is a late light, not a weeks-ahead warning.",
  },
};

const EXTRA_INDICATORS: Array<[string, string, string, string | null]> = [
  ["SOFR − IORB", "Funding break", "Secured overnight financing rate versus interest on reserve balances.", "funding"],
  ["DTB3 − DFF", "Policy cliff", "3-month Treasury bill versus the federal funds rate.", "policy"],
  ["VIXCLS − VXVCLS", "Crowded unwind", "VIX versus 3-month VIX. Backwardation means stress is already priced.", "unwind"],
  ["USREC", "Recession shading", "NBER recession months. Drawn behind every chart, not used as a live signal.", null],
];

let shockInfoBound = false;

function closeShockPops(): void {
  document.querySelectorAll<HTMLElement>(".sfe-market-shock-pop").forEach((pop) => {
    pop.hidden = true;
  });
  document.querySelectorAll<HTMLButtonElement>(".sfe-market-shock-info").forEach((button) => {
    button.setAttribute("aria-expanded", "false");
  });
}

let shockModalOpener: HTMLButtonElement | null = null;

function closeShockModal(): void {
  const modal = document.querySelector<HTMLElement>(".sfe-market-modal");
  if (!modal || modal.hidden) return;
  modal.hidden = true;
  const opener = shockModalOpener;
  shockModalOpener = null;
  opener?.setAttribute("aria-expanded", "false");
  opener?.focus();
}

function openShockModal(button: HTMLButtonElement): void {
  const tile = button.closest(".sfe-market-shock");
  const copy = tile?.querySelector<HTMLElement>(".sfe-market-shock-copy");
  const modal = document.querySelector<HTMLElement>(".sfe-market-modal");
  const title = modal?.querySelector(".sfe-market-modal-title");
  const body = modal?.querySelector(".sfe-market-modal-body");
  if (!tile || !copy || !modal || !title || !body) return;
  title.textContent = tile.querySelector("strong")?.textContent ?? "Shock watch";
  body.innerHTML = copy.innerHTML;
  shockModalOpener?.setAttribute("aria-expanded", "false");
  shockModalOpener = button;
  button.setAttribute("aria-expanded", "true");
  modal.hidden = false;
  modal.querySelector<HTMLButtonElement>(".sfe-market-modal-close")?.focus();
}

function bindShockInfo(): void {
  if (shockInfoBound || typeof document === "undefined") return;
  shockInfoBound = true;
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const openButton = target.closest<HTMLButtonElement>(".sfe-market-shock-open");
    if (openButton) {
      event.preventDefault();
      event.stopPropagation();
      closeShockPops();
      openShockModal(openButton);
      return;
    }
    if (target.closest(".sfe-market-modal-close") || target.classList.contains("sfe-market-modal")) {
      event.preventDefault();
      closeShockModal();
      return;
    }
    if (target.closest(".sfe-market-modal")) return;
    const button = target.closest<HTMLButtonElement>(".sfe-market-shock-info");
    if (button) {
      event.preventDefault();
      event.stopPropagation();
      const popId = button.getAttribute("aria-controls");
      const pop = popId ? document.getElementById(popId) : null;
      const open = button.getAttribute("aria-expanded") === "true";
      closeShockPops();
      if (!open && pop) {
        pop.hidden = false;
        button.setAttribute("aria-expanded", "true");
      }
      return;
    }
    if (!target.closest(".sfe-market-shock-pop")) closeShockPops();
  });
  document.addEventListener("dblclick", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest("button, a, input, textarea")) return;
    const card = target.closest<HTMLElement>("[data-market-card]");
    if (!card?.dataset.marketCard) return;
    event.preventDefault();
    openCardHistory(card.dataset.marketCard);
  });
  document.addEventListener("keydown", (event) => {
    if (event.key !== "Escape") return;
    const chartPop = document.querySelector<HTMLElement>(".sfe-market-chart-pop");
    if (chartPop && !chartPop.hidden) return;
    const modal = document.querySelector<HTMLElement>(".sfe-market-modal");
    if (modal && !modal.hidden) {
      event.preventDefault();
      closeShockModal();
      return;
    }
    closeShockPops();
  });
}

function esc(value: unknown): string {
  return String(value ?? "")
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll('"', "&quot;");
}

function usd(value: number): string {
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
const WEEK_MS = 7 * 86400000;
const YEAR_MS = 365.25 * 86400000;

function monthLabel(iso: string): string {
  const [year, month] = iso.slice(0, 7).split("-");
  const name = MONTH_SHORT[Number(month) - 1] ?? month;
  return `${name} ${year}`;
}

function probeDateLabel(ms: number, withDay: boolean): string {
  const iso = new Date(ms).toISOString().slice(0, 10);
  if (!withDay) return monthLabel(iso);
  const [year, month, day] = iso.split("-");
  const name = MONTH_SHORT[Number(month) - 1] ?? month;
  return `${name} ${Number(day)}, ${year}`;
}

/** Monday on or after this instant, in UTC. */
function utcMondayOnOrAfter(ms: number): number {
  const date = new Date(ms);
  const day = date.getUTCDay();
  const forward = day === 1 ? 0 : day === 0 ? 1 : 8 - day;
  return Date.UTC(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate() + forward);
}

/** Grid and labels that get finer as more pixels are available, down to weeks. */
function dateAxis(options: {
  xFor: (iso: string) => number;
  top: number;
  bottom: number;
  left: number;
  right: number;
  t0: number;
  t1: number;
}): { grid: string; ticks: string } {
  const plotW = Math.max(1, options.right - options.left);
  const span = Math.max(1, options.t1 - options.t0);
  const px = (ms: number) => (ms / span) * plotW;
  const y = options.bottom + 14;
  const lines: string[] = [];
  const labels: string[] = [];
  const label = (x: number, text: string) => {
    if (x < options.left + 14 || x > options.right - 14) return;
    labels.push(
      `<text class="sfe-market-tick" x="${x.toFixed(1)}" y="${y}" fill="#9aa0a6" font-size="11" text-anchor="middle">${text}</text>`,
    );
  };
  const vline = (ms: number, kind: "year" | "month" | "week") => {
    const iso = new Date(ms).toISOString().slice(0, 10);
    const x = options.xFor(iso);
    if (x <= options.left + 0.4 || x >= options.right - 0.4) return x;
    lines.push(
      `<line class="sfe-market-grid sfe-market-grid-${kind}" x1="${x.toFixed(1)}" y1="${options.top}" x2="${x.toFixed(1)}" y2="${options.bottom}"></line>`,
    );
    return x;
  };
  if (px(WEEK_MS) >= 56) {
    for (let cursor = utcMondayOnOrAfter(options.t0); cursor <= options.t1; cursor += WEEK_MS) {
      const date = new Date(cursor);
      const day = date.getUTCDate();
      const month = date.getUTCMonth();
      const firstOfMonth = day <= 7;
      const x = vline(cursor, firstOfMonth ? "month" : "week");
      const text = firstOfMonth
        ? month === 0
          ? `Jan ${date.getUTCFullYear()}`
          : MONTH_SHORT[month] ?? ""
        : String(day);
      label(x, text);
    }
  } else if (px(YEAR_MS / 12) >= 42) {
    const startYear = new Date(options.t0).getUTCFullYear();
    const endYear = new Date(options.t1).getUTCFullYear();
    for (let year = startYear; year <= endYear; year += 1) {
      for (let month = 0; month < 12; month += 1) {
        const stamp = Date.UTC(year, month, 1);
        if (stamp < options.t0 || stamp > options.t1) continue;
        const x = vline(stamp, month === 0 ? "year" : "month");
        label(x, month === 0 ? String(year) : MONTH_SHORT[month] ?? "");
      }
    }
  } else if (px(YEAR_MS) >= 32) {
    const startYear = new Date(options.t0).getUTCFullYear();
    const endYear = new Date(options.t1).getUTCFullYear();
    for (let year = startYear; year <= endYear; year += 1) {
      const x = vline(Date.UTC(year, 0, 1), "year");
      label(x, String(year));
    }
  } else {
    const startYear = new Date(options.t0).getUTCFullYear();
    const endYear = new Date(options.t1).getUTCFullYear();
    for (let year = startYear; year <= endYear; year += 1) {
      vline(Date.UTC(year, 0, 1), year % 5 === 0 ? "year" : "month");
    }
    for (let year = Math.ceil(startYear / 5) * 5; year <= endYear; year += 5) {
      label(options.xFor(`${year}-01-01`), String(year));
    }
  }
  return { grid: lines.join(""), ticks: labels.join("") };
}

function directionText(direction: MarketRiskIndicator["direction"]): string {
  if (direction === "up") return "higher than 3 months ago";
  if (direction === "down") return "lower than 3 months ago";
  return "little changed versus 3 months ago";
}

const SHOCK_STATUS_LABEL: Record<string, string> = {
  safe: "Safe",
  tight: "Tight",
  watch: "Watch",
  off: "Off",
};

function renderGaugeMark(status: string | undefined, reading: string | null | undefined): string {
  if (!status || !SHOCK_STATUS_LABEL[status]) return "";
  const value = reading ? `<span class="sfe-market-shock-reading">${esc(reading)}</span>` : "";
  return `<div class="sfe-market-shock-status"><b class="sfe-market-shock-flag sfe-market-shock-flag-${esc(status)}">${esc(SHOCK_STATUS_LABEL[status])}</b>${value}</div>`;
}

function vsReading(current: string, limit: string, hot: boolean): string {
  const body = `${esc(current)} <span>vs</span> ${esc(limit)}`;
  return hot ? `<span class="sfe-market-vs-hot">${body}</span>` : body;
}

function renderGaugeTile(name: string, status: string, readingHtml: string, copyHtml: string, cardKey: string): string {
  const known = Boolean(SHOCK_STATUS_LABEL[status]);
  const tone = known ? status : "off";
  const flag = known
    ? `<b class="sfe-market-shock-flag sfe-market-shock-flag-${esc(tone)}">${esc(SHOCK_STATUS_LABEL[tone])}</b>`
    : "";
  const card = cardKey ? ` data-market-card="${esc(cardKey)}" title="Double-click for history"` : "";
  return `<div class="sfe-market-shock sfe-market-shock-${esc(tone)}"${card}><div class="sfe-market-shock-top"><strong>${esc(name)}</strong><button type="button" class="sfe-market-shock-open" aria-expanded="false" aria-label="About ${esc(name)}">i</button></div>${flag}<span class="sfe-market-shock-reading">${readingHtml}</span><div class="sfe-market-shock-copy" hidden>${copyHtml}</div></div>`;
}

function shockCopy(shock: MarketRiskSnapshot["shocks"][number]): string {
  const help = SHOCK_HELP[shock.id];
  const rows: Array<[string, string]> = help
    ? [
        ["What it is", help.what],
        ["How it works", help.how],
        ["Normal", help.normal],
        ["Dangerous", help.dangerous],
      ]
    : [];
  return [`<p>${esc(shock.detail)}</p>`, ...rows.map(([label, text]) => `<p><b>${esc(label)}</b> ${esc(text)}</p>`)].join("");
}

function renderShock(shock: MarketRiskSnapshot["shocks"][number]): string {
  const status = shock.status && SHOCK_STATUS_LABEL[shock.status] ? shock.status : shock.active ? "watch" : "safe";
  const compare =
    shock.reading && shock.limit
      ? vsReading(shock.reading, shock.limit, status === "watch")
      : esc(shock.reading ?? "—");
  return renderGaugeTile(shock.name, status, compare, shockCopy(shock), `shock:${shock.id}`);
}

function riskStatus(value: number | null | undefined): "safe" | "tight" | "watch" | "off" {
  if (value == null || !Number.isFinite(value)) return "off";
  if (value >= 30) return "watch";
  if (value >= 20) return "tight";
  return "safe";
}

function renderRiskTile(blend: MarketRiskBlend | null | undefined): string {
  const latest = blend?.risk?.latest;
  if (latest == null || !Number.isFinite(latest)) return "";
  const shown = `${latest.toFixed(1)}%`;
  const copy = [
    `<p>Latest ${esc(shown)}, the same reading as the orange line on the recession blend chart.</p>`,
    `<p><b>What it shows</b> ${esc(LEGEND_HELP.risk)}</p>`,
    `<p><b>How to read it</b> 30% or more is Watch. From 20% up to that line is Tight. Below 20% is Safe. It is not the NY Fed percent.</p>`,
  ].join("");
  const hot = riskStatus(latest) === "watch";
  return renderGaugeTile("Risk", riskStatus(latest), vsReading(shown, "30%", hot), copy, "risk");
}

function renderIndicatorTile(indicator: MarketRiskIndicator): string {
  const when = indicator.latestDate ? ` on ${monthLabel(indicator.latestDate)}` : "";
  const percentile =
    indicator.percentile20y == null ? "" : ` ${indicator.percentile20y}% of the last 20 years were at or below this level.`;
  const parts = [
    `<p>${esc(indicator.role)}. Latest ${esc(indicator.latestDisplay)}${esc(when)}, ${esc(directionText(indicator.direction))}.${esc(percentile)}</p>`,
    `<p><b>What it shows</b> ${esc(indicator.note)}</p>`,
  ];
  if (indicator.correlationText) parts.push(`<p><b>Recessions</b> ${esc(indicator.correlationText)}</p>`);
  const status = indicator.status && SHOCK_STATUS_LABEL[indicator.status] ? indicator.status : "off";
  const danger = GAUGE_BANDS[indicator.id]?.danger ?? (indicator.id === "oil" ? "+25% in 3 mo" : null);
  const reading = danger
    ? vsReading(indicator.latestDisplay, danger, status === "watch")
    : esc(indicator.latestDisplay);
  return renderGaugeTile(indicator.name, status, reading, parts.join(""), `indicator:${indicator.id}`);
}

function renderExtraTile(series: string, role: string, note: string, shock: MarketRiskSnapshot["shocks"][number] | undefined): string {
  const status = shock?.status && SHOCK_STATUS_LABEL[shock.status] ? shock.status : "";
  const reading =
    shock?.reading && shock.limit
      ? vsReading(shock.reading, shock.limit, status === "watch")
      : shock?.reading
        ? esc(shock.reading)
        : esc(series === "USREC" ? "Shading" : "—");
  const help = shock ? SHOCK_HELP[shock.id] : undefined;
  const parts = [`<p>${esc(note)}</p>`, `<p><b>Used for</b> ${esc(role)}. Series ${esc(series)}.</p>`];
  if (shock?.detail) parts.push(`<p>${esc(shock.detail)}</p>`);
  if (help) {
    parts.push(
      `<p><b>What it is</b> ${esc(help.what)}</p>`,
      `<p><b>How it works</b> ${esc(help.how)}</p>`,
      `<p><b>Normal</b> ${esc(help.normal)}</p>`,
      `<p><b>Dangerous</b> ${esc(help.dangerous)}</p>`,
    );
  }
  return renderGaugeTile(series, status, reading, parts.join(""), shock ? `shock:${shock.id}` : "");
}

function stressGapLine(score: number): string {
  const below = 65 - score;
  if (below > 0) return `${below} below the cut line`;
  if (below === 0) return "On the cut line";
  const past = -below;
  return past === 1 ? "1 past the cut line" : `${past} past the cut line`;
}

function postureText(snapshot: MarketRiskSnapshot): string {
  const used = snapshot.margin?.used;
  const usedText =
    used == null || !Number.isFinite(used) ? "" : ` Margin used ${usd(used)}.`;
  if (snapshot.posture === "calm") return `Margin posture: no ramp-down trigger.${usedText}`;
  if (snapshot.posture === "tighten") return `Margin posture: stop adding short-put collateral.${usedText}`;
  if (snapshot.posture === "cut") return `Margin posture: cut margin before adding risk.${usedText}`;
  return "Margin posture is unavailable until the gauges refresh.";
}

function timeValue(iso: string): number {
  return Date.parse(`${iso.slice(0, 10)}T00:00:00Z`);
}

const BLEND_COLORS: Record<string, string> = {
  curve: "#8ab4f8",
  credit: "#fdd663",
  conditions: "#81c995",
  labor: "#c58af9",
  lending: "#ef9a9a",
  permits: "#4db6ac",
  orders: "#ffb74d",
  expectations: "#ba68c8",
  blend: "#f1f3f4",
  risk: "#e67e22",
};

const SERIES_COLORS: Record<string, string> = {
  t10y3m: "#8ab4f8",
  recession: "#e67e22",
  baa: "#fdd663",
  nfci: "#81c995",
  lending: "#ef9a9a",
  permits: "#4db6ac",
  orders: "#ffb74d",
  expectations: "#ba68c8",
  sahm: "#c58af9",
  claims: "#f48fb1",
  vix: "#90a4ae",
  oil: "#bcaaa4",
};

let blendTogglesBound = false;
let blendChartModel: {
  blend: MarketRiskBlend;
  recessions: MarketRiskRecession[];
  indicators: MarketRiskIndicator[];
} | null = null;

type BlendRange = "all" | "5y" | "1y" | "1q";

function applyBlendRange(scroll: HTMLElement, range: BlendRange): void {
  if (!blendChartModel) return;
  const chart = scroll.closest(".sfe-market-chart");
  const off = new Set<string>();
  chart?.querySelectorAll<HTMLButtonElement>(".sfe-market-line-toggle.is-off").forEach((button) => {
    if (button.dataset.line) off.add(button.dataset.line);
  });
  scroll.classList.toggle("is-zoom", range !== "all");
  scroll.innerHTML = renderBlendChart(
    blendChartModel.blend,
    blendChartModel.recessions,
    range,
    blendChartModel.indicators,
  );
  const svg = scroll.querySelector<SVGSVGElement>("svg");
  if (svg && range !== "all") {
    const host = Math.max(320, scroll.clientWidth);
    const view = svg.viewBox.baseVal;
    const height = Math.round((host * view.height) / 640);
    scroll.style.setProperty("--plot-height", `${height}px`);
    svg.style.width = `${Math.round((host * view.width) / 640)}px`;
    svg.style.height = `${height}px`;
    svg.style.maxWidth = "none";
  }
  off.forEach((line) => {
    scroll.querySelectorAll(`[data-line="${line}"]`).forEach((node) => node.classList.add("is-off"));
  });
  if (!svg || range === "all") {
    scroll.scrollLeft = 0;
    return;
  }
  const nowX = Number(svg.dataset.nowX);
  const viewWidth = svg.viewBox.baseVal.width;
  const cssWidth = Number.parseFloat(svg.style.width);
  const nowPx = (nowX / viewWidth) * cssWidth;
  scroll.scrollLeft = Math.max(0, nowPx - scroll.clientWidth + 28);
}

function applyPopBlendRange(range: BlendRange): void {
  if (!blendChartModel) return;
  const els = chartPopEls();
  if (!els || els.pop.hidden) return;
  const scroll = els.stage.querySelector<HTMLElement>(".sfe-market-plot-scroll");
  if (!scroll) return;
  const off = new Set<string>();
  els.pop.querySelectorAll<HTMLButtonElement>(".sfe-market-line-toggle.is-off").forEach((button) => {
    if (button.dataset.line) off.add(button.dataset.line);
  });
  scroll.classList.toggle("is-zoom", range !== "all");
  scroll.innerHTML = renderBlendChart(blendChartModel.blend, blendChartModel.recessions, range, blendChartModel.indicators);
  scroll.querySelectorAll("svg").forEach((svg) => {
    if (!(svg instanceof SVGSVGElement)) return;
    svg.style.width = "";
    svg.style.height = "";
    svg.style.maxWidth = "none";
  });
  off.forEach((line) => {
    scroll.querySelectorAll(`[data-line="${line}"]`).forEach((node) => node.classList.add("is-off"));
  });
  layoutChartPop();
  const svg = scroll.querySelector("svg");
  if (!(svg instanceof SVGSVGElement) || range === "all") {
    scroll.scrollLeft = 0;
    return;
  }
  const nowX = Number(svg.dataset.nowX);
  const viewWidth = svg.viewBox.baseVal.width;
  const cssWidth = Number.parseFloat(svg.style.width);
  if (!viewWidth || !cssWidth) return;
  const nowPx = (nowX / viewWidth) * cssWidth;
  scroll.scrollLeft = Math.max(0, nowPx - scroll.clientWidth + 28);
}

function bindBlendToggles(): void {
  if (blendTogglesBound || typeof document === "undefined") return;
  blendTogglesBound = true;
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const rangeButton = target.closest<HTMLButtonElement>(".sfe-market-range button");
    if (rangeButton) {
      const range = rangeButton.dataset.range;
      if (range !== "all" && range !== "5y" && range !== "1y" && range !== "1q") return;
      event.preventDefault();
      rangeButton.parentElement?.querySelectorAll("button").forEach((button) => {
        button.setAttribute("aria-pressed", button === rangeButton ? "true" : "false");
      });
      if (rangeButton.closest(".sfe-market-chart-pop")) {
        applyPopBlendRange(range);
        return;
      }
      const scroll = rangeButton.closest(".sfe-market-chart")?.querySelector<HTMLElement>(".sfe-market-plot-scroll");
      if (scroll) applyBlendRange(scroll, range);
      return;
    }
    const button = target.closest<HTMLButtonElement>(".sfe-market-line-toggle");
    if (!button) return;
    const chart = button.closest(".sfe-market-chart-pop") ?? button.closest(".sfe-market-chart");
    const line = button.dataset.line;
    if (!chart || !line) return;
    event.preventDefault();
    const off = button.getAttribute("aria-pressed") !== "false";
    button.setAttribute("aria-pressed", off ? "false" : "true");
    button.classList.toggle("is-off", off);
    chart.querySelectorAll(`[data-line="${line}"]`).forEach((node) => {
      node.classList.toggle("is-off", off);
    });
  });
  bindChartProbe();
}

let chartProbeBound = false;

function chartProbeMarkup(top: number, bottom: number): string {
  return `<g class="sfe-market-probe" visibility="hidden"><rect class="sfe-market-probe-strip" y="${top}" height="${(bottom - top).toFixed(1)}" fill="rgba(255,255,255,0.16)"></rect><rect class="sfe-market-probe-tag" rx="4" ry="4" fill="rgba(15,17,22,0.94)" stroke="rgba(255,255,255,0.4)"></rect><text class="sfe-market-probe-label" fill="#f1f3f4" font-size="12"></text></g>`;
}

function bindChartProbe(): void {
  if (chartProbeBound || typeof document === "undefined") return;
  chartProbeBound = true;
  const hide = () => {
    document.querySelectorAll(".sfe-market-probe").forEach((node) => node.setAttribute("visibility", "hidden"));
  };
  document.addEventListener("pointermove", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) {
      hide();
      return;
    }
    const svg = target.closest("svg[data-probe]");
    if (!(svg instanceof SVGSVGElement)) {
      hide();
      return;
    }
    const left = Number(svg.dataset.left);
    const right = Number(svg.dataset.right);
    const top = Number(svg.dataset.top);
    const bottom = Number(svg.dataset.bottom);
    const t0 = Number(svg.dataset.t0);
    const t1 = Number(svg.dataset.t1);
    const rect = svg.getBoundingClientRect();
    const view = svg.viewBox.baseVal;
    if (!rect.width || !view.width || !(right > left) || !(t1 > t0)) {
      hide();
      return;
    }
    const x = ((event.clientX - rect.left) / rect.width) * view.width;
    const y = ((event.clientY - rect.top) / rect.height) * view.height;
    if (x < left || x > right || y < top || y > bottom + 16) {
      hide();
      return;
    }
    const ms = t0 + ((x - left) / (right - left)) * (t1 - t0);
    const px = rect.width / view.width;
    const weekPx = (WEEK_MS / (t1 - t0)) * (right - left) * px;
    const fine = weekPx >= 36;
    let stripX: number;
    let stripW: number;
    let label: string;
    if (fine) {
      stripX = x;
      stripW = Math.max(1.5, Math.min(8, WEEK_MS / (t1 - t0) * (right - left) * 0.15));
      label = probeDateLabel(ms, true);
    } else {
      const date = new Date(ms);
      let year = date.getUTCFullYear();
      let month = date.getUTCMonth() + (date.getUTCDate() >= 15 ? 1 : 0);
      let monthStart = Date.UTC(year, month, 1);
      if (monthStart < t0) monthStart = t0;
      if (monthStart > t1) monthStart = t1;
      const monthDate = new Date(monthStart);
      year = monthDate.getUTCFullYear();
      month = monthDate.getUTCMonth();
      stripX = left + ((monthStart - t0) / (t1 - t0)) * (right - left);
      stripW = 8;
      label = monthLabel(`${year}-${String(month + 1).padStart(2, "0")}-01`);
    }
    const probe = svg.querySelector(".sfe-market-probe");
    const strip = svg.querySelector(".sfe-market-probe-strip");
    const tag = svg.querySelector(".sfe-market-probe-tag");
    const text = svg.querySelector(".sfe-market-probe-label");
    if (!(probe instanceof SVGGElement) || !(strip instanceof SVGRectElement) || !(tag instanceof SVGRectElement) || !(text instanceof SVGTextElement)) return;
    hide();
    const width = stripW;
    strip.setAttribute("x", Math.max(left, Math.min(right - width, stripX - width / 2)).toFixed(1));
    strip.setAttribute("width", String(width));
    text.setAttribute("font-size", (15 / px).toFixed(2));
    text.textContent = label;
    const tagWidth = text.getComputedTextLength() + 14 / px;
    let textX = stripX + 8;
    if (textX + tagWidth > right - 2) textX = stripX - tagWidth - 4;
    if (textX < left) textX = left;
    text.setAttribute("x", textX.toFixed(1));
    text.setAttribute("y", (top + 18 / px).toFixed(1));
    tag.setAttribute("x", (textX - 7 / px).toFixed(1));
    tag.setAttribute("y", (top + 3 / px).toFixed(1));
    tag.setAttribute("width", tagWidth.toFixed(1));
    tag.setAttribute("height", (22 / px).toFixed(2));
    tag.setAttribute("rx", (5 / px).toFixed(2));
    tag.setAttribute("ry", (5 / px).toFixed(2));
    probe.setAttribute("visibility", "visible");
  });
}

let chartPopBound = false;
let chartPopZoom = 1;
let chartPopSeries: { indicator: MarketRiskIndicator; recessions: MarketRiskRecession[] } | null = null;
type ChartPopAnchor = number | "end";

function chartPopZoomCap(): number {
  const span = chartPopSeries ? historySpan(chartPopSeries.indicator.history) : null;
  if (!span) return 12;
  const host = Math.max(280, chartPopEls()?.stage.clientWidth || 640);
  const pxPerWeek = (WEEK_MS / (span.t1 - span.t0)) * host;
  return Math.max(1, 72 / Math.max(pxPerWeek, 0.25));
}

function chartPopAnchor(stage: HTMLElement, clientX?: number): ChartPopAnchor {
  if (stage.scrollWidth <= stage.clientWidth + 2) return "end";
  const rect = stage.getBoundingClientRect();
  const local = clientX == null ? stage.clientWidth / 2 : clientX - rect.left;
  const contentX = stage.scrollLeft + Math.max(0, Math.min(stage.clientWidth, local));
  return stage.scrollWidth > 0 ? contentX / stage.scrollWidth : "end";
}

function zoomWindowLabel(spanMs: number, zoom: number): string {
  const visible = spanMs / Math.max(zoom, 1);
  if (visible <= WEEK_MS * 1.35) return "1 week";
  if (visible < 8 * WEEK_MS) return `${Math.max(2, Math.round(visible / WEEK_MS))} weeks`;
  const months = visible / (YEAR_MS / 12);
  if (months < 20) return `${Math.max(1, Math.round(months))} mo`;
  const years = visible / YEAR_MS;
  if (years < 1.4) return "1 year";
  return `${Math.round(years)} years`;
}

function chartPopEls(): { pop: HTMLElement; stage: HTMLElement; card: HTMLElement } | null {
  const pop = document.querySelector<HTMLElement>(".sfe-market-chart-pop");
  const stage = pop?.querySelector<HTMLElement>(".sfe-market-chart-pop-stage");
  const card = pop?.querySelector<HTMLElement>(".sfe-market-chart-pop-card");
  if (!pop || !stage || !card) return null;
  return { pop, stage, card };
}

function layoutChartPop(anchor: ChartPopAnchor = "end"): void {
  const els = chartPopEls();
  if (!els) return;
  if (chartPopSeries) {
    const hostW = Math.max(280, els.stage.clientWidth || 640);
    const hostH = Math.max(180, els.stage.clientHeight || 220);
    const cap = chartPopZoomCap();
    chartPopZoom = Math.min(cap, Math.max(1, chartPopZoom));
    const width = Math.max(hostW, Math.round(hostW * chartPopZoom));
    const plot = `<div class="sfe-market-plot">${renderChart(chartPopSeries.indicator, chartPopSeries.recessions, null, { width, height: hostH })}</div>`;
    els.stage.innerHTML = plot;
    const svg = els.stage.querySelector("svg");
    if (svg instanceof SVGSVGElement) {
      svg.style.width = `${width}px`;
      svg.style.height = `${hostH}px`;
      svg.style.maxWidth = "none";
    }
    const maxScroll = Math.max(0, els.stage.scrollWidth - els.stage.clientWidth);
    if (anchor === "end") els.stage.scrollLeft = maxScroll;
    else els.stage.scrollLeft = Math.max(0, Math.min(maxScroll, anchor * els.stage.scrollWidth - els.stage.clientWidth / 2));
    const span = historySpan(chartPopSeries.indicator.history);
    const label = els.pop.querySelector(".sfe-market-chart-pop-zoom");
    if (label) {
      label.textContent = span ? zoomWindowLabel(span.t1 - span.t0, chartPopZoom) : "All";
      label.title = "Zoom in to a week, then scroll sideways.";
    }
    return;
  }
  const svg = els.stage.querySelector("svg");
  if (!(svg instanceof SVGSVGElement)) return;
  const view = svg.viewBox.baseVal;
  if (!view.width || !view.height) return;
  const host = els.stage.querySelector(".sfe-market-plot-scroll") ?? els.stage;
  const fitW = Math.max(280, host.clientWidth);
  const frame = Number(svg.dataset.window) || view.width;
  const screen = Boolean(document.fullscreenElement) || els.pop.classList.contains("is-screen") || els.card.classList.contains("is-screen");
  const fitHeight = (fitW * view.height) / frame;
  const height = screen ? Math.max(120, els.stage.clientHeight) : fitHeight;
  const width = fitW * chartPopZoom * (view.width / frame);
  svg.setAttribute("preserveAspectRatio", "none");
  svg.style.width = `${Math.round(width)}px`;
  svg.style.height = `${Math.round(height)}px`;
  svg.style.maxWidth = "none";
  const tickSize = Math.max(2.5, (8 * view.height) / height);
  svg.querySelectorAll<SVGTextElement>(".sfe-market-tick").forEach((node) => {
    node.setAttribute("font-size", tickSize.toFixed(2));
  });
  const label = els.pop.querySelector(".sfe-market-chart-pop-zoom");
  if (label) label.textContent = `${Math.round(chartPopZoom * 100)}%`;
}

function pinChartPopToNewest(): void {
  const stage = chartPopEls()?.stage;
  if (!stage || !chartPopSeries) return;
  stage.scrollLeft = Math.max(0, stage.scrollWidth - stage.clientWidth);
}

function setChartPopZoom(next: number, clientX?: number): void {
  const stage = chartPopEls()?.stage;
  // Series history grows toward the past. Keep the latest observation on the right edge.
  const anchor: ChartPopAnchor = chartPopSeries ? "end" : stage ? chartPopAnchor(stage, clientX) : "end";
  const cap = chartPopZoomCap();
  chartPopZoom = Math.min(cap, Math.max(1, Math.round(next * 100) / 100));
  layoutChartPop(anchor);
  if (chartPopSeries) requestAnimationFrame(() => pinChartPopToNewest());
}

function layoutChartPopKeepingPlace(): void {
  const stage = chartPopEls()?.stage;
  layoutChartPop(stage ? chartPopAnchor(stage) : "end");
}

function fillChartPop(): void {
  layoutChartPopKeepingPlace();
}

function setChartGridSolo(on: boolean): void {
  const solo = window.sfeDesktop?.setGridSolo;
  if (!solo) return;
  void solo(on).catch(() => undefined);
}

function closeChartPop(): void {
  const els = chartPopEls();
  if (!els || els.pop.hidden) return;
  setChartGridSolo(false);
  if (document.fullscreenElement) void document.exitFullscreen();
  els.card.classList.remove("is-screen");
  els.pop.classList.remove("is-screen");
  els.pop.hidden = true;
  els.stage.replaceChildren();
  els.pop.querySelector(".sfe-market-chart-pop-legend")?.replaceChildren();
  const range = els.pop.querySelector<HTMLElement>(".sfe-market-chart-pop-range");
  if (range) range.hidden = true;
  chartPopZoom = 1;
  chartPopSeries = null;
}

function chartPopLegend(chart: Element): HTMLElement | null {
  const legend = chart.querySelector(".sfe-market-legend");
  if (!(legend instanceof HTMLElement)) return null;
  const copy = legend.cloneNode(true);
  if (!(copy instanceof HTMLElement)) return null;
  copy.querySelectorAll<HTMLElement>("[id]").forEach((node) => {
    const id = node.id;
    if (!id) return;
    const next = `${id}-chartpop`;
    copy.querySelectorAll(`[aria-controls="${id}"]`).forEach((button) => {
      button.setAttribute("aria-controls", next);
    });
    node.id = next;
  });
  return copy;
}

function openChartPop(
  chart: Element,
  series?: { indicator: MarketRiskIndicator; recessions: MarketRiskRecession[] },
): void {
  const els = chartPopEls();
  if (!els) return;
  const plot = chart.querySelector(".sfe-market-plot") ?? chart.querySelector("svg[data-probe]");
  if (!plot) return;
  const title = els.pop.querySelector(".sfe-market-chart-pop-title");
  if (title) title.textContent = chart.querySelector("h3")?.textContent?.replace(/\s+/g, " ").trim() || "Chart";
  const legendHost = els.pop.querySelector(".sfe-market-chart-pop-legend");
  if (legendHost) {
    legendHost.replaceChildren();
    const legend = chartPopLegend(chart);
    if (legend) legendHost.append(legend);
  }
  const rangeHost = els.pop.querySelector<HTMLElement>(".sfe-market-chart-pop-range");
  const sourceRange = chart.querySelector(".sfe-market-range");
  if (rangeHost) {
    rangeHost.hidden = !(sourceRange instanceof HTMLElement);
    if (sourceRange instanceof HTMLElement) {
      rangeHost.querySelectorAll("button").forEach((button) => {
        const match = sourceRange.querySelector(`button[data-range="${button.dataset.range}"]`);
        button.setAttribute("aria-pressed", match?.getAttribute("aria-pressed") === "true" ? "true" : "false");
      });
    }
  }
  els.stage.innerHTML = plot.outerHTML;
  els.stage.querySelectorAll("svg").forEach((svg) => {
    if (!(svg instanceof SVGSVGElement)) return;
    svg.style.width = "";
    svg.style.height = "";
    svg.style.maxWidth = "none";
  });
  chartPopSeries = series ?? null;
  chartPopZoom = 1;
  els.pop.hidden = false;
  layoutChartPop("end");
  requestAnimationFrame(() => layoutChartPop(chartPopSeries ? "end" : "end"));
  els.pop.querySelector<HTMLButtonElement>(".sfe-market-chart-pop-close")?.focus();
}

async function openChartFullScreen(): Promise<void> {
  const els = chartPopEls();
  if (!els) return;
  const on = Boolean(document.fullscreenElement) || els.pop.classList.contains("is-screen");
  if (on) {
    setChartGridSolo(false);
    if (document.fullscreenElement) await document.exitFullscreen();
    els.pop.classList.remove("is-screen");
    els.card.classList.remove("is-screen");
    requestAnimationFrame(() => layoutChartPopKeepingPlace());
    return;
  }
  els.pop.classList.add("is-screen");
  els.card.classList.add("is-screen");
  if (window.sfeDesktop?.setGridSolo) {
    setChartGridSolo(true);
    requestAnimationFrame(() => fillChartPop());
    return;
  }
  try {
    await els.card.requestFullscreen();
  } catch {
    requestAnimationFrame(() => fillChartPop());
  }
}

function bindChartPop(): void {
  if (chartPopBound || typeof document === "undefined") return;
  chartPopBound = true;
  document.addEventListener("contextmenu", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest(".sfe-market-chart-pop")) {
      event.preventDefault();
      return;
    }
    if (target.closest("button, a, input, textarea")) return;
    const chart = target.closest(".sfe-market-chart");
    if (!chart?.querySelector("svg[data-probe]")) return;
    event.preventDefault();
    openChartPop(chart);
  });
  document.addEventListener("click", (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    const pop = document.querySelector<HTMLElement>(".sfe-market-chart-pop");
    if (!pop || pop.hidden) return;
    if (target.classList.contains("sfe-market-chart-pop") || target.closest(".sfe-market-chart-pop-close")) {
      event.preventDefault();
      closeChartPop();
      return;
    }
    const zoom = target.closest<HTMLElement>("[data-chart-zoom]");
    if (!zoom) return;
    event.preventDefault();
    const action = zoom.dataset.chartZoom;
    if (action === "in") setChartPopZoom(chartPopZoom * 1.5);
    else if (action === "out") setChartPopZoom(chartPopZoom / 1.5);
    else if (action === "fit") setChartPopZoom(1);
    else if (action === "screen") void openChartFullScreen();
  });
  document.addEventListener(
    "keydown",
    (event) => {
      if (event.key !== "Escape") return;
      const pop = document.querySelector<HTMLElement>(".sfe-market-chart-pop");
      if (!pop || pop.hidden) return;
      event.preventDefault();
      event.stopPropagation();
      if (document.fullscreenElement) {
        void document.exitFullscreen();
        return;
      }
      closeChartPop();
    },
    true,
  );
  document.addEventListener("fullscreenchange", () => {
    const els = chartPopEls();
    if (!els || els.pop.hidden) return;
    const full = document.fullscreenElement === els.card;
    els.card.classList.toggle("is-screen", full);
    els.pop.classList.toggle("is-screen", full);
    requestAnimationFrame(() => {
      if (full) fillChartPop();
      else layoutChartPopKeepingPlace();
    });
  });
  document.addEventListener(
    "wheel",
    (event) => {
      const pop = document.querySelector<HTMLElement>(".sfe-market-chart-pop");
      if (!pop || pop.hidden) return;
      const stage = pop.querySelector(".sfe-market-chart-pop-stage");
      if (!(stage instanceof HTMLElement) || !(event.target instanceof Node) || !stage.contains(event.target)) return;
      if (event.ctrlKey || event.metaKey) {
        event.preventDefault();
        setChartPopZoom(chartPopZoom * (event.deltaY > 0 ? 1 / 1.1 : 1.1), event.clientX);
        return;
      }
      if (!chartPopSeries || stage.scrollWidth <= stage.clientWidth + 2) return;
      const delta = Math.abs(event.deltaX) > Math.abs(event.deltaY) ? event.deltaX : event.deltaY;
      if (!delta) return;
      event.preventDefault();
      stage.scrollLeft += delta;
    },
    { passive: false },
  );
}

const NBER_LEGEND =
  "The red band is a recession dated by the NBER, from FRED series USREC. It shows when a recession was already underway. The shading is history, not a forecast.";

const RECESSION_BAND = "rgba(255,48,48,0.62)";
const BEFORE_RECESSION_BAND = "rgba(255,204,0,0.46)";

const LEGEND_HELP: Record<string, string> = {
  future:
    "The block on the right is the next 12 months, drawn on the same time scale as the history. It fills when the blend is at or above 55, the level that sat under most of the year before past recessions. A clear block means the latest blend is below that line.",
  blend:
    "The white line ranks each input on one 0–100 scale. 100 is the most recession-like reading in that series' own history. The mix is 36% the 12-month recession chance, 12% the Baa spread, 10% financial conditions, 12% the Sahm gap, 10% lending standards, 8% permit growth, 8% core orders, and 4% consumer sentiment. The dotted white line at 55 is the pre-recession band. This is a rank, not a probability.",
  lending:
    "The rose line is the net share of banks tightening C&I loan standards, ranked against its own history. A high reading means more banks were tightening than easing.",
  permits:
    "The teal line is building permits versus a year earlier, ranked against its own history. A high reading means permits had fallen the way they do before recessions.",
  orders:
    "The amber line is core capital-goods orders versus a year earlier, ranked against its own history. A high reading means orders had fallen the way they do before recessions.",
  expectations:
    "The purple line is Michigan consumer sentiment, ranked against its own history. A high reading means households were more pessimistic than usual. It has a small weight because sentiment has fallen hard without a recession before.",
  risk:
    "The orange line is the historical chance a recession is already underway or starts within 12 months, read from the blend. It is fit on past months and applied to the latest blend. It is not the NY Fed percent.",
  curve:
    "The blue line is the NY Fed 12-month recession probability, ranked against its own history. A high reading means the yield curve looked more like the months before past recessions.",
  credit:
    "The gold line is Moody's Baa corporate spread versus the 10-year, ranked against its own history. A high reading means lenders were charging more than usual for credit risk.",
  conditions:
    "The green line is the Chicago Fed financial conditions index, ranked against its own history. A high reading means funding and markets were tighter than usual.",
  labor:
    "The purple line is the Sahm unemployment gap, ranked against its own history. A high reading means unemployment has already risen the way it does in a recession. It confirms; it does not lead by many months.",
};

function legendItem(body: string, key: string, about: string, text: string): string {
  const popId = `sfe-market-legend-pop-${key}`;
  return `<div class="sfe-market-legend-item">${body}<button type="button" class="sfe-market-shock-info" aria-expanded="false" aria-controls="${popId}" aria-label="About ${esc(about)}">i</button><div id="${popId}" class="sfe-market-shock-pop sfe-market-legend-pop" hidden><p>${esc(text)}</p></div></div>`;
}

function legendMark(swatch: string, label: string, key: string, text: string): string {
  return legendItem(`<i class="sfe-market-swatch ${swatch}"></i>${esc(label)}`, key, label, text);
}

function lineToggle(id: string, swatch: string, label: string, text: string, dotted = false): string {
  const dash = dotted ? `<i class="sfe-market-swatch ${swatch} sfe-market-swatch-dash"></i>` : "";
  const button = `<button type="button" class="sfe-market-line-toggle" data-line="${esc(id)}" aria-pressed="true"><i class="sfe-market-swatch ${swatch}"></i>${dash}${esc(label)}</button>`;
  return legendItem(button, `blend-${id}`, label, text);
}

/** Watch and tight levels, matching gaugeStatus on the server. */
const GAUGE_BANDS: Record<
  string,
  {
    tight: number;
    watch: number;
    higherIsWorse: boolean;
    danger: string;
    safeText: string;
    tightText: string;
    watchText: string;
  }
> = {
  stress: {
    tight: 35,
    watch: 65,
    higherIsWorse: true,
    danger: "65",
    safeText: "The green band is below 35. That is clear of the cut-margin line.",
    tightText: "The yellow band is 35 up to 65. That is more than halfway to cutting margin.",
    watchText: "The red band is 65 and above, the cut-margin line.",
  },
  t10y3m: {
    tight: 0.5,
    watch: 0,
    higherIsWorse: false,
    danger: "0 pp",
    safeText: "The green band is 0.50 pp and above. The curve is not close to inverting.",
    tightText: "The yellow band is from 0 up to 0.50 pp. The curve is close to inverting.",
    watchText: "The red band is 0 and below. The curve is inverted.",
  },
  recession: {
    tight: 20,
    watch: 30,
    higherIsWorse: true,
    danger: "30%",
    safeText: "The green band is below 20%. That is clear of the danger line.",
    tightText: "The yellow band is 20% up to 30%. That is more than halfway to the danger line.",
    watchText: "The red band is 30% and above, the danger line for the 12-month recession chance.",
  },
  sahm: {
    tight: 0.25,
    watch: 0.5,
    higherIsWorse: true,
    danger: "0.50 pp",
    safeText: "The green band is below 0.25. That is clear of the Sahm trigger.",
    tightText: "The yellow band is 0.25 up to 0.50. Unemployment has risen part of the way to the trigger.",
    watchText: "The red band is 0.50 and above, the Sahm trigger. A recession is usually already underway.",
  },
  baa: {
    tight: 3,
    watch: 4,
    higherIsWorse: true,
    danger: "4 pp",
    safeText: "The green band is below 3 pp. That is clear of the danger line.",
    tightText: "The yellow band is 3 pp up to 4 pp. Lenders are charging more than usual.",
    watchText: "The red band is 4 pp and above. Across past recessions this spread averaged about 3.4 pp, and 4 pp is the danger line.",
  },
  nfci: {
    tight: 0,
    watch: 1,
    higherIsWorse: true,
    danger: "1",
    safeText: "The green band is 0 and below. Conditions are looser than average.",
    tightText: "The yellow band is above 0 up to 1. Conditions are tighter than average.",
    watchText: "The red band is above 1, the danger line for financial conditions.",
  },
  claims: {
    tight: 350_000,
    watch: 500_000,
    higherIsWorse: true,
    danger: "500,000",
    safeText: "The green band is below 350,000. That is clear of the danger line.",
    tightText: "The yellow band is 350,000 up to 500,000.",
    watchText: "The red band is 500,000 and above, the danger line for initial claims.",
  },
  vix: {
    tight: 20,
    watch: 30,
    higherIsWorse: true,
    danger: "30",
    safeText: "The green band is below 20. That is clear of the danger line.",
    tightText: "The yellow band is 20 up to 30.",
    watchText: "The red band is 30 and above, the danger line for the VIX.",
  },
  lending: {
    tight: 0,
    watch: 20,
    higherIsWorse: true,
    danger: "20 pp",
    safeText: "The green band is below 0. More banks were easing than tightening.",
    tightText: "The yellow band is from 0 up to 20 pp. More banks were tightening than easing.",
    watchText: "The red band is 20 pp and above, the danger line for lending standards.",
  },
  permits: {
    tight: 0,
    watch: -15,
    higherIsWorse: false,
    danger: "−15%",
    safeText: "The green band is 0% and above. Permits are up from a year earlier.",
    tightText: "The yellow band is from −15% up to 0%. Permits are falling from a year earlier.",
    watchText: "The red band is −15% and below, the danger line for building permits.",
  },
  orders: {
    tight: 0,
    watch: -10,
    higherIsWorse: false,
    danger: "−10%",
    safeText: "The green band is 0% and above. Core orders are up from a year earlier.",
    tightText: "The yellow band is from −10% up to 0%. Core orders are falling from a year earlier.",
    watchText: "The red band is −10% and below, the danger line for core capital-goods orders.",
  },
  expectations: {
    tight: 80,
    watch: 70,
    higherIsWorse: false,
    danger: "70",
    safeText: "The green band is 80 and above. Households are not especially pessimistic.",
    tightText: "The yellow band is from 70 up to 80. Sentiment is soft.",
    watchText: "The red band is 70 and below, the danger line for consumer sentiment.",
  },
  risk: {
    tight: 20,
    watch: 30,
    higherIsWorse: true,
    danger: "30%",
    safeText: "The green band is below 20%. That is clear of the Watch line.",
    tightText: "The yellow band is 20% up to 30%.",
    watchText: "The red band is 30% and above. It is not the NY Fed percent.",
  },
  funding: {
    tight: 0.075,
    watch: 0.15,
    higherIsWorse: true,
    danger: "+0.15 pp",
    safeText: "The green band is below 0.075 pp. SOFR is near interest on reserves.",
    tightText: "The yellow band is 0.075 pp up to 0.15 pp.",
    watchText: "The red band is above 0.15 pp, where the funding watch opens.",
  },
  credit: {
    tight: 0.2,
    watch: 0.4,
    higherIsWorse: true,
    danger: "+0.40 pp",
    safeText: "The green band is below 0.20 pp versus the prior 3-month average.",
    tightText: "The yellow band is 0.20 pp up to 0.40 pp above that average.",
    watchText: "The red band is above 0.40 pp, where the credit watch opens.",
  },
  energy: {
    tight: 12.5,
    watch: 25,
    higherIsWorse: true,
    danger: "+25%",
    safeText: "The green band is a rise of less than 12.5% versus three months ago.",
    tightText: "The yellow band is a rise of 12.5% up to 25%.",
    watchText: "The red band is a rise above 25%, where the energy watch opens.",
  },
  policy: {
    tight: 0.2,
    watch: 0.4,
    higherIsWorse: true,
    danger: "0.40 pp",
    safeText: "The green band is a gap under 0.20 pp versus the fed-funds rate.",
    tightText: "The yellow band is a gap of 0.20 pp up to 0.40 pp.",
    watchText: "The red band is a gap beyond 0.40 pp, where the policy watch opens.",
  },
  unwind: {
    tight: -1,
    watch: 0,
    higherIsWorse: true,
    danger: "0 pts",
    safeText: "The green band is spot VIX more than 1 point below 3-month VIX.",
    tightText: "The yellow band is spot VIX within 1 point below 3-month VIX.",
    watchText: "The red band is spot VIX above 3-month VIX, where the unwind watch opens.",
  },
};

const ZONE_FILL = {
  safe: "rgba(129,201,149,0.22)",
  tight: "rgba(255,204,0,0.24)",
  watch: "rgba(255,48,48,0.28)",
};

/** Blend part → the indicator whose Watch level is the dotted danger line. */
const BLEND_PART_DANGER: Record<string, { indicatorId: string; mark: string }> = {
  curve: { indicatorId: "recession", mark: "30%" },
  credit: { indicatorId: "baa", mark: "4 pp" },
  conditions: { indicatorId: "nfci", mark: "1" },
  labor: { indicatorId: "sahm", mark: "0.50" },
  lending: { indicatorId: "lending", mark: "20 pp" },
  permits: { indicatorId: "permits", mark: "−15%" },
  orders: { indicatorId: "orders", mark: "−10%" },
  expectations: { indicatorId: "expectations", mark: "70" },
};

function dangerRankOf(history: MarketRiskPoint[], threshold: number, higherIsWorse = true): number | null {
  const values = history.map((point) => point.value).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (values.length < 24) return null;
  let asBad = 0;
  for (const value of values) if (higherIsWorse ? value <= threshold : value >= threshold) asBad += 1;
  return (100 * asBad) / values.length;
}

function blendPartDanger(
  indicators: MarketRiskIndicator[],
  partId: string,
): { rank: number; mark: string } | null {
  const spec = BLEND_PART_DANGER[partId];
  if (!spec) return null;
  const band = GAUGE_BANDS[spec.indicatorId];
  const row = indicators.find((indicator) => indicator.id === spec.indicatorId);
  if (!band || !row) return null;
  const rank = dangerRankOf(row.history, band.watch, band.higherIsWorse);
  if (rank == null) return null;
  return { rank, mark: spec.mark };
}

function shiftMonths(iso: string, months: number): string {
  const [year, month, day] = iso.slice(0, 10).split("-").map(Number);
  return new Date(Date.UTC(year, month - 1 + months, day || 1)).toISOString().slice(0, 10);
}

function clipOpenInterval(
  start: number,
  end: number,
  blocks: { start: number; end: number }[],
): { start: number; end: number }[] {
  let pieces = end > start ? [{ start, end }] : [];
  for (const block of blocks) {
    const next: { start: number; end: number }[] = [];
    for (const piece of pieces) {
      if (block.end <= piece.start || block.start >= piece.end) {
        next.push(piece);
        continue;
      }
      if (block.start > piece.start) next.push({ start: piece.start, end: block.start });
      if (block.end < piece.end) next.push({ start: block.end, end: piece.end });
    }
    pieces = next;
  }
  return pieces;
}

/** Yellow year-before bands, then a brighter red recession band, drawn under the lines. */
function historyShadeRects(
  recessions: MarketRiskRecession[],
  xFor: (iso: string) => number,
  top: number,
  height: number,
  plotStartMs: number,
  plotEndMs: number,
): string {
  const during = recessions
    .map((span) => ({
      start: timeValue(span.start),
      end: span.end ? timeValue(span.end) : plotEndMs,
    }))
    .filter((span) => span.end > span.start);
  const rect = (start: number, end: number, fill: string): string => {
    const clippedStart = Math.max(start, plotStartMs);
    const clippedEnd = Math.min(end, plotEndMs);
    if (clippedEnd <= clippedStart) return "";
    const x = xFor(new Date(clippedStart).toISOString().slice(0, 10));
    const width = xFor(new Date(clippedEnd).toISOString().slice(0, 10)) - x;
    if (width < 0.5) return "";
    return `<rect x="${x.toFixed(1)}" y="${top}" width="${width.toFixed(1)}" height="${height.toFixed(1)}" fill="${fill}"></rect>`;
  };
  const before = during.flatMap((span) => {
    const leadStart = timeValue(shiftMonths(new Date(span.start).toISOString().slice(0, 10), -3));
    return clipOpenInterval(leadStart, span.start, during);
  });
  const yellow = before.map((span) => rect(span.start, span.end, BEFORE_RECESSION_BAND)).join("");
  const red = during.map((span) => rect(span.start, span.end, RECESSION_BAND)).join("");
  return `${yellow}${red}`;
}

/** Vertical dotted borders. Months only when the window is short enough that they stay readable. */
function timeGrid(options: {
  xFor: (iso: string) => number;
  top: number;
  bottom: number;
  left: number;
  right: number;
  t0: number;
  t1: number;
  years: boolean;
  months: boolean;
}): string {
  const lines: string[] = [];
  const vline = (isoDate: string, kind: "year" | "month") => {
    const x = options.xFor(isoDate);
    if (x <= options.left + 0.4 || x >= options.right - 0.4) return;
    lines.push(
      `<line class="sfe-market-grid sfe-market-grid-${kind}" x1="${x.toFixed(1)}" y1="${options.top}" x2="${x.toFixed(1)}" y2="${options.bottom}"></line>`,
    );
  };
  const startYear = new Date(options.t0).getUTCFullYear();
  const endYear = new Date(options.t1).getUTCFullYear();
  if (options.years) {
    for (let year = startYear; year <= endYear; year += 1) vline(`${year}-01-01`, "year");
  }
  if (options.months) {
    for (let year = startYear; year <= endYear; year += 1) {
      for (let month = options.years ? 1 : 0; month < 12; month += 1) {
        const stamp = Date.UTC(year, month, 1);
        if (stamp < options.t0 || stamp > options.t1) continue;
        vline(new Date(stamp).toISOString().slice(0, 10), "month");
      }
    }
  }
  return lines.join("");
}

function renderBlendChart(
  blend: MarketRiskBlend,
  recessions: MarketRiskRecession[],
  range: BlendRange = "all",
  indicators: MarketRiskIndicator[] = [],
): string {
  const points = blend.history.filter((point) => Number.isFinite(point.value));
  if (points.length < 2 || !blend.futureStart || !blend.futureEnd) return "";
  const height = 248;
  const left = 8;
  const top = 12;
  const bottom = 218;
  const t0 = timeValue(points[0].date);
  const tNow = timeValue(points[points.length - 1].date);
  const t1 = timeValue(blend.futureEnd);
  const total = Math.max(1, t1 - t0);
  const yearMs = 365.25 * 24 * 60 * 60 * 1000;
  const windowMs = range === "1y" ? yearMs : range === "5y" ? yearMs * 5 : range === "1q" ? yearMs / 4 : total;
  const plot = 604 * (total / windowMs);
  const right = left + plot;
  const width = right + 8;
  const xFor = (iso: string) => left + ((timeValue(iso) - t0) / total) * (right - left);
  const yFor = (value: number) => bottom - (Math.max(0, Math.min(100, value)) / 100) * (bottom - top);
  const iso = (ms: number) => new Date(ms).toISOString().slice(0, 10);
  const bands = historyShadeRects(recessions, xFor, top, bottom - top, t0, tNow);
  const futureX = xFor(points[points.length - 1].date);
  const futureW = Math.max(1, right - futureX);
  const futureFill = blend.inDanger ? "rgba(224,106,106,0.45)" : "rgba(255,255,255,0.04)";
  const future = `<rect x="${futureX.toFixed(1)}" y="${top}" width="${futureW.toFixed(1)}" height="${(bottom - top).toFixed(1)}" fill="${futureFill}" stroke="${blend.inDanger ? "rgba(224,106,106,0.9)" : "rgba(255,255,255,0.2)"}" stroke-dasharray="3 3"></rect>`;
  const usedLabelY: number[] = [];
  const guideLine = (lineId: string, rank: number, color: string, label: string): string => {
    const y = yFor(rank);
    let labelY = y - 1;
    for (const used of usedLabelY) {
      if (Math.abs(labelY - used) < 8) labelY = used - 8;
    }
    labelY = Math.min(bottom - 2, Math.max(top + 8, labelY));
    usedLabelY.push(labelY);
    return `<line data-line="${esc(lineId)}" x1="${left}" y1="${y.toFixed(1)}" x2="${right}" y2="${y.toFixed(1)}" stroke="${color}" stroke-width="1.15" stroke-dasharray="2 2.5"></line><text class="sfe-market-tick" data-line="${esc(lineId)}" x="${(right - 3).toFixed(1)}" y="${labelY.toFixed(1)}" fill="${color}" font-size="8" text-anchor="end">${esc(label)}</text>`;
  };
  const thresholdGuides = [
    guideLine("blend", blend.threshold, BLEND_COLORS.blend, String(blend.threshold)),
    ...blend.parts.map((part) => {
      const danger = blendPartDanger(indicators, part.id);
      if (!danger) return "";
      return guideLine(part.id, danger.rank, BLEND_COLORS[part.id] ?? "#9aa0a6", danger.mark);
    }),
  ];
  const guides = [
    ...thresholdGuides,
    `<line x1="${futureX.toFixed(1)}" y1="${top}" x2="${futureX.toFixed(1)}" y2="${bottom}" stroke="rgba(255,255,255,0.35)"></line>`,
  ];
  const partLines = blend.parts
    .map((part) => {
      const line = part.history
        .filter((point) => Number.isFinite(point.value))
        .map((point, index) => `${index === 0 ? "M" : "L"}${xFor(point.date).toFixed(1)},${yFor(point.value).toFixed(1)}`)
        .join("");
      const color = BLEND_COLORS[part.id] ?? "#9aa0a6";
      return `<path data-line="${esc(part.id)}" d="${line}" fill="none" stroke="${color}" stroke-width="0.7" opacity="0.85"></path>`;
    })
    .join("");
  const blendLine = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${xFor(point.date).toFixed(1)},${yFor(point.value).toFixed(1)}`)
    .join("");
  const riskPoints = (blend.risk?.history ?? []).filter((point) => Number.isFinite(point.value));
  const riskLine = riskPoints
    .map((point, index) => `${index === 0 ? "M" : "L"}${xFor(point.date).toFixed(1)},${yFor(point.value).toFixed(1)}`)
    .join("");
  const ticks: string[] = [];
  const pushTick = (isoDate: string, label: string) => {
    const x = xFor(isoDate);
    if (x < left + 18 || x > right - 18) return;
    ticks.push(`<text class="sfe-market-tick" x="${x.toFixed(1)}" y="236" fill="#9aa0a6" font-size="8" text-anchor="middle">${label}</text>`);
  };
  const grid = timeGrid({
    xFor,
    top,
    bottom,
    left,
    right,
    t0,
    t1,
    years: true,
    months: range !== "all",
  });
  if (range === "1q") {
    const names = ["Jan", "Apr", "Jul", "Oct"];
    const startYear = Number(points[0].date.slice(0, 4));
    const endYear = new Date(t1).getUTCFullYear();
    for (let year = startYear; year <= endYear; year += 1) {
      for (let slot = 0; slot < 4; slot += 1) {
        const stamp = Date.UTC(year, slot * 3, 1);
        if (stamp < t0 || stamp > t1) continue;
        pushTick(iso(stamp), `${names[slot]} ${String(year).slice(2)}`);
      }
    }
  } else {
    const step = range === "all" ? 5 : 1;
    const startYear = Number(points[0].date.slice(0, 4));
    const endYear = Number(blend.futureEnd.slice(0, 4));
    for (let year = Math.ceil(startYear / step) * step; year <= endYear; year += step) pushTick(`${year}-01-01`, String(year));
  }
  if (futureW > 72) {
    ticks.push(
      `<text x="${(futureX + futureW / 2).toFixed(1)}" y="${(top + 16).toFixed(1)}" fill="${blend.inDanger ? "#f6d4d4" : "#9aa0a6"}" font-size="11" text-anchor="middle">Next 12 mo</text>`,
    );
  }
  const windowAttr = range === "all" ? "" : ` data-window="640"`;
  return `<svg data-probe="1" data-now-x="${futureX.toFixed(1)}" data-left="${left}" data-right="${right.toFixed(1)}" data-top="${top}" data-bottom="${bottom}" data-t0="${Math.round(t0)}" data-t1="${Math.round(t1)}"${windowAttr} viewBox="0 0 ${width.toFixed(1)} ${height}" role="img" aria-label="Blended recession chart. ${esc(blend.correlationText ?? "")}">${bands}${future}${grid}${guides.join("")}${partLines}<path data-line="blend" d="${blendLine}" fill="none" stroke="#f1f3f4" stroke-width="1.25"></path>${riskLine ? `<path data-line="risk" d="${riskLine}" fill="none" stroke="#e67e22" stroke-width="1.25"></path>` : ""}${ticks.join("")}${chartProbeMarkup(top, bottom)}</svg>`;
}

function renderBlend(blend: MarketRiskBlend, recessions: MarketRiskRecession[], indicators: MarketRiskIndicator[]): string {
  const share = (value: number | null) => (value == null ? "—" : `${value}%`);
  const zone = blend.inDanger
    ? `The latest blend is ${blend.latest ?? "—"}, at or above ${blend.threshold}, so the next 12 months are marked as the danger window. That window is the horizon this band was measured on, not a scheduled recession.`
    : `The latest blend is ${blend.latest ?? "—"}, below ${blend.threshold}, so the next 12 months stay clear.`;
  const riskNote =
    blend.risk == null
      ? ""
      : ` Risk, the orange line, is the historical chance a recession is already underway or starts within 12 months, read from the blend. Latest ${blend.risk.latest}%. It is not the NY Fed percent.`;
  const toggles = [
    lineToggle("blend", "sfe-market-swatch-blend", "Blend", LEGEND_HELP.blend, true),
    ...(blend.risk ? [lineToggle("risk", "sfe-market-swatch-risk", "Risk", LEGEND_HELP.risk)] : []),
    ...blend.parts.map((part) => {
      const danger = blendPartDanger(indicators, part.id);
      const base = LEGEND_HELP[part.id] ?? part.name;
      const text = danger
        ? `${base} The dotted line, ${danger.mark}, is this indicator's danger level, drawn where that level ranks in its own history (${Math.round(danger.rank)}). It turns off with this line.`
        : base;
      return lineToggle(part.id, `sfe-market-swatch-${part.id}`, part.name, text, danger != null);
    }),
  ];
  bindBlendToggles();
  blendChartModel = { blend, recessions, indicators };
  const chart = renderBlendChart(blend, recessions, "all", indicators);
  if (!chart) return "";
  return `<section class="sfe-market-chart">
    <div class="sfe-market-chart-head"><h3>Recession blend</h3><div class="sfe-market-range" role="group" aria-label="History window"><button type="button" data-range="all" aria-pressed="true">All</button><button type="button" data-range="5y" aria-pressed="false">5 years</button><button type="button" data-range="1y" aria-pressed="false">1 year</button><button type="button" data-range="1q" aria-pressed="false">1 quarter</button></div></div>
    <p class="sfe-market-note">Each line is that series ranked against its own history, so 100 is its most recession-like reading. The white line is 36% the 12-month recession chance, 12% the Baa spread, 10% financial conditions, 12% the Sahm gap, 10% lending standards, 8% permit growth, 8% core orders, and 4% consumer sentiment. For permits, orders, and sentiment, a fall is ranked as more recession-like. The dotted white line at ${blend.threshold} is the pre-recession band: ${share(blend.beforeShare)} of the year-before months sat on or above it, ${share(blend.duringShare)} of recession months, and ${share(blend.otherShare)} of the other months. Each other dotted line is that indicator's danger level, in its color, drawn where that level ranks here. Click a name to hide that line and its dotted mark. A shorter window scrolls, so you can move back through older history.${esc(riskNote)}</p>
    <div class="sfe-market-legend">${legendMark("sfe-market-swatch-recession", "NBER recession", "blend-recession", NBER_LEGEND)}${legendMark("sfe-market-swatch-future", blend.inDanger ? "Next 12 months, danger" : "Next 12 months, clear", "blend-future", LEGEND_HELP.future)}${toggles.join("")}</div>
    <div class="sfe-market-plot"><div class="sfe-market-plot-axis" aria-hidden="true"><span>100</span><span>0</span></div><div class="sfe-market-plot-scroll">${chart}</div></div>
    <p class="sfe-market-note">${esc(zone)}${blend.correlationText ? ` ${esc(blend.correlationText)}` : ""}</p>
  </section>`;
}

function historySpan(points: MarketRiskPoint[]): { t0: number; t1: number } | null {
  let t0 = Infinity;
  let t1 = -Infinity;
  for (const point of points) {
    if (!Number.isFinite(point.value)) continue;
    const t = timeValue(point.date);
    if (!Number.isFinite(t)) continue;
    if (t < t0) t0 = t;
    if (t > t1) t1 = t;
  }
  if (!Number.isFinite(t0) || !(t1 > t0)) return null;
  return { t0, t1 };
}

/** One time window for every indicator chart, so a recession lands on the same x. */
function sharedChartDomain(indicators: MarketRiskIndicator[]): { t0: number; t1: number } | null {
  let t0 = Infinity;
  let t1 = -Infinity;
  for (const indicator of indicators) {
    const span = historySpan(indicator.history);
    if (!span) continue;
    t0 = Math.min(t0, span.t0);
    t1 = Math.max(t1, span.t1);
  }
  if (!Number.isFinite(t0) || !(t1 > t0)) return null;
  return { t0, t1 };
}

function renderChart(
  indicator: MarketRiskIndicator,
  recessions: MarketRiskRecession[],
  domain: { t0: number; t1: number } | null = null,
  frame?: { width: number; height: number },
): string {
  const points = indicator.history.filter((point) => Number.isFinite(point.value));
  if (points.length < 2) return "";
  const framed = Boolean(frame);
  const width = frame?.width ?? 640;
  const height = frame?.height ?? 168;
  const left = 8;
  const right = width - 8;
  const top = 10;
  const bottom = height - (framed ? 22 : 18);
  const own = historySpan(points);
  const t0 = domain?.t0 ?? own?.t0 ?? timeValue(points[0].date);
  const t1 = domain?.t1 ?? own?.t1 ?? timeValue(points[points.length - 1].date);
  const span = Math.max(1, t1 - t0);
  const xFor = (iso: string) => left + ((timeValue(iso) - t0) / span) * (right - left);
  let min = Math.min(...points.map((point) => point.value));
  let max = Math.max(...points.map((point) => point.value));
  if (indicator.chart.zeroLine) {
    min = Math.min(min, 0);
    max = Math.max(max, 0);
  }
  if (indicator.chart.threshold != null) {
    min = Math.min(min, indicator.chart.threshold);
    max = Math.max(max, indicator.chart.threshold);
  }
  const band = GAUGE_BANDS[indicator.id];
  if (band) {
    min = Math.min(min, band.tight, band.watch);
    max = Math.max(max, band.tight, band.watch);
  }
  if (max - min < 1e-6) {
    max += 1;
    min -= 1;
  }
  const pad = (max - min) * 0.08;
  min -= pad;
  max += pad;
  const yFor = (value: number) => bottom - ((value - min) / (max - min)) * (bottom - top);
  const zoneSpans: Array<[number, number, string]> = band
    ? band.higherIsWorse
      ? [
          [Number.NEGATIVE_INFINITY, band.tight, ZONE_FILL.safe],
          [band.tight, band.watch, ZONE_FILL.tight],
          [band.watch, Number.POSITIVE_INFINITY, ZONE_FILL.watch],
        ]
      : [
          [Number.NEGATIVE_INFINITY, band.watch, ZONE_FILL.watch],
          [band.watch, band.tight, ZONE_FILL.tight],
          [band.tight, Number.POSITIVE_INFINITY, ZONE_FILL.safe],
        ]
    : [];
  const zones = zoneSpans
    .map(([lo, hi, fill]) => {
      const a = Math.max(lo, min);
      const b = Math.min(hi, max);
      if (!(b > a)) return "";
      const yStart = yFor(a);
      const yEnd = yFor(b);
      const y = Math.max(top, Math.min(yStart, yEnd));
      const yBottom = Math.min(bottom, Math.max(yStart, yEnd));
      const heightPx = yBottom - y;
      if (heightPx < 0.4) return "";
      return `<rect x="${left}" y="${y.toFixed(1)}" width="${(right - left).toFixed(1)}" height="${heightPx.toFixed(1)}" fill="${fill}"></rect>`;
    })
    .join("");
  const bands = historyShadeRects(recessions, xFor, top, bottom - top, t0, t1);
  const line = points
    .map((point, index) => `${index === 0 ? "M" : "L"}${xFor(point.date).toFixed(1)},${yFor(point.value).toFixed(1)}`)
    .join("");
  const guides: string[] = [];
  const onBandEdge = (value: number) =>
    band != null && (Math.abs(value - band.tight) < 1e-6 || Math.abs(value - band.watch) < 1e-6);
  if ((indicator.chart.zeroLine || indicator.chart.threshold === 0) && !onBandEdge(0)) {
    const y = yFor(0);
    guides.push(`<line x1="${left}" y1="${y.toFixed(1)}" x2="${right}" y2="${y.toFixed(1)}" stroke="rgba(255,255,255,0.28)" stroke-dasharray="3 3"></line>`);
  }
  if (band) {
    const tightY = yFor(band.tight);
    const watchY = yFor(band.watch);
    guides.push(`<line x1="${left}" y1="${tightY.toFixed(1)}" x2="${right}" y2="${tightY.toFixed(1)}" stroke="rgba(255,204,0,0.95)" stroke-width="1" stroke-dasharray="4 3"></line>`);
    guides.push(`<line x1="${left}" y1="${watchY.toFixed(1)}" x2="${right}" y2="${watchY.toFixed(1)}" stroke="rgba(255,48,48,0.95)" stroke-width="1.1" stroke-dasharray="4 3"></line>`);
  } else if (indicator.chart.threshold != null && indicator.chart.threshold !== 0) {
    const y = yFor(indicator.chart.threshold);
    guides.push(`<line x1="${left}" y1="${y.toFixed(1)}" x2="${right}" y2="${y.toFixed(1)}" stroke="rgba(224,106,106,0.85)" stroke-dasharray="4 3"></line>`);
  }
  let grid = "";
  let ticks = "";
  if (framed) {
    const axis = dateAxis({ xFor, top, bottom, left, right, t0, t1 });
    grid = axis.grid;
    ticks = axis.ticks;
  } else {
    const startYear = new Date(t0).getUTCFullYear();
    const endYear = new Date(t1).getUTCFullYear();
    const tickNodes: string[] = [];
    grid = timeGrid({ xFor, top, bottom, left, right, t0, t1, years: true, months: false });
    for (let year = Math.ceil(startYear / 5) * 5; year <= endYear; year += 5) {
      const x = xFor(`${year}-01-01`);
      if (x < left + 24 || x > right - 24) continue;
      tickNodes.push(`<text class="sfe-market-tick" x="${x.toFixed(1)}" y="${(bottom + 14).toFixed(0)}" fill="#9aa0a6" font-size="8" text-anchor="middle">${year}</text>`);
    }
    ticks = tickNodes.join("");
  }
  const label = `${indicator.name}. ${indicator.correlationText ?? ""}`;
  return `<svg data-probe="1" data-left="${left}" data-right="${right}" data-top="${top}" data-bottom="${bottom}" data-t0="${Math.round(t0)}" data-t1="${Math.round(t1)}" viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(label)}">${zones}${bands}${grid}${guides.join("")}<path d="${line}" fill="none" stroke="#e67e22" stroke-width="1.6"></path>${ticks}${chartProbeMarkup(top, bottom)}</svg>`;
}

const CHART_GROUPS: Array<{ id: string; title: string; note: string; ids: string[] }> = [
  {
    id: "rates",
    title: "Rates",
    note: "The yield curve and the NY Fed 12-month chance, each ranked so 100 is its most recession-like reading. An inverted curve sits high on this chart.",
    ids: ["t10y3m", "recession"],
  },
  {
    id: "credit",
    title: "Credit and lending",
    note: "The Baa spread, financial conditions, and bank lending standards. 100 is the most recession-like reading in each series' own history.",
    ids: ["baa", "nfci", "lending"],
  },
  {
    id: "economy",
    title: "Real economy",
    note: "Building permits, core capital-goods orders, and Michigan sentiment. A fall is ranked as more recession-like, so 100 is the weakest reading in that series' history.",
    ids: ["permits", "orders", "expectations"],
  },
  {
    id: "labor",
    title: "Labor",
    note: "The Sahm gap confirms a recession. Initial claims move sooner, and they also jump in shocks that do not become recessions.",
    ids: ["sahm", "claims"],
  },
  {
    id: "markets",
    title: "Market stress",
    note: "VIX and oil. These usually rise with a shock that is already here. They are not in the recession blend.",
    ids: ["vix", "oil"],
  },
];

function recessionRank(history: MarketRiskPoint[], higherIsWorse: boolean): MarketRiskPoint[] {
  const values = history.map((point) => point.value).filter((value) => Number.isFinite(value)).sort((a, b) => a - b);
  if (values.length < 24) return [];
  return history
    .filter((point) => Number.isFinite(point.value))
    .map((point) => {
      let asBad = 0;
      for (const value of values) if (higherIsWorse ? value <= point.value : value >= point.value) asBad += 1;
      return { date: point.date, value: (100 * asBad) / values.length };
    });
}

function renderRankedGroup(
  group: (typeof CHART_GROUPS)[number],
  indicators: MarketRiskIndicator[],
  recessions: MarketRiskRecession[],
  domain: { t0: number; t1: number } | null,
): string {
  const rows = group.ids
    .map((id) => indicators.find((indicator) => indicator.id === id))
    .filter((indicator): indicator is MarketRiskIndicator => Boolean(indicator));
  if (!rows.length) return "";
  const ranked = rows.map((indicator) => ({
    indicator,
    points: recessionRank(indicator.history, GAUGE_BANDS[indicator.id]?.higherIsWorse !== false),
  }));
  const width = 640;
  const height = 168;
  const left = 8;
  const right = 632;
  const top = 10;
  const bottom = 150;
  const own = historySpan(ranked.flatMap((row) => row.points));
  const t0 = domain?.t0 ?? own?.t0 ?? 0;
  const t1 = domain?.t1 ?? own?.t1 ?? 1;
  const span = Math.max(1, t1 - t0);
  const xFor = (iso: string) => left + ((timeValue(iso) - t0) / span) * (right - left);
  const yFor = (value: number) => bottom - (Math.max(0, Math.min(100, value)) / 100) * (bottom - top);
  const bands = historyShadeRects(recessions, xFor, top, bottom - top, t0, t1);
  const paths = ranked
    .map(({ indicator, points }) => {
      if (points.length < 2) return "";
      const color = SERIES_COLORS[indicator.id] ?? "#9aa0a6";
      const line = points
        .map((point, index) => `${index === 0 ? "M" : "L"}${xFor(point.date).toFixed(1)},${yFor(point.value).toFixed(1)}`)
        .join("");
      const band = GAUGE_BANDS[indicator.id];
      const danger = band ? dangerRankOf(indicator.history, band.watch, band.higherIsWorse) : null;
      const guide =
        danger == null
          ? ""
          : `<line data-line="${esc(indicator.id)}" x1="${left}" y1="${yFor(danger).toFixed(1)}" x2="${right}" y2="${yFor(danger).toFixed(1)}" stroke="${color}" stroke-dasharray="2 2.5"></line>`;
      return `${guide}<path data-line="${esc(indicator.id)}" d="${line}" fill="none" stroke="${color}" stroke-width="1.1"></path>`;
    })
    .join("");
  const grid = timeGrid({ xFor, top, bottom, left, right, t0, t1, years: true, months: false });
  const ticks: string[] = [];
  const startYear = new Date(t0).getUTCFullYear();
  const endYear = new Date(t1).getUTCFullYear();
  for (let year = Math.ceil(startYear / 5) * 5; year <= endYear; year += 5) {
    const x = xFor(`${year}-01-01`);
    if (x < left + 24 || x > right - 24) continue;
    ticks.push(`<text class="sfe-market-tick" x="${x.toFixed(1)}" y="164" fill="#9aa0a6" font-size="8" text-anchor="middle">${year}</text>`);
  }
  const toggles = ranked
    .map(({ indicator }) => {
      const band = GAUGE_BANDS[indicator.id];
      const text = band
        ? `${indicator.name}. ${indicator.note} Danger is ${band.danger}.`
        : `${indicator.name}. ${indicator.note}`;
      const swatch = indicator.id === "recession" ? "sfe-market-swatch-nyfed" : `sfe-market-swatch-${indicator.id}`;
      return lineToggle(indicator.id, swatch, indicator.name, text, band != null);
    })
    .join("");
  const svg = `<svg data-probe="1" data-left="${left}" data-right="${right}" data-top="${top}" data-bottom="${bottom}" data-t0="${Math.round(t0)}" data-t1="${Math.round(t1)}" viewBox="0 0 ${width} ${height}" role="img" aria-label="${esc(group.title)}">${bands}${grid}${paths}${ticks.join("")}${chartProbeMarkup(top, bottom)}</svg>`;
  return `<section class="sfe-market-chart">
    <div class="sfe-market-chart-head"><h3>${esc(group.title)}</h3></div>
    <p class="sfe-market-note">${esc(group.note)}</p>
    <div class="sfe-market-legend">${legendMark("sfe-market-swatch-recession", "NBER recession", `${group.id}-recession`, NBER_LEGEND)}${toggles}</div>
    <div class="sfe-market-plot"><div class="sfe-market-plot-axis" aria-hidden="true"><span>100</span><span>0</span></div>${svg}</div>
  </section>`;
}

function renderIndicator(
  indicator: MarketRiskIndicator,
  recessions: MarketRiskRecession[],
  domain: { t0: number; t1: number } | null,
): string {
  const series = indicator.seriesId ? ` · ${indicator.seriesId}` : "";
  const percentile =
    indicator.percentile20y == null
      ? ""
      : ` ${indicator.percentile20y}% of the last 20 years were at or below this level.`;
  const zoneBand = GAUGE_BANDS[indicator.id];
  const zoneLegend = zoneBand
    ? `${legendMark("sfe-market-swatch-safe", "Safe", `${indicator.id}-safe`, zoneBand.safeText)}${legendMark("sfe-market-swatch-tight", "Tight", `${indicator.id}-tight`, zoneBand.tightText)}${legendMark("sfe-market-swatch-watch", "Danger", `${indicator.id}-watch`, zoneBand.watchText)}`
    : "";
  return `<section class="sfe-market-chart">
    <div class="sfe-market-chart-head"><h3>${esc(indicator.name)}<span class="sfe-market-series">${esc(series)}</span></h3>${renderGaugeMark(indicator.status, indicator.latestDisplay)}</div>
    <p class="sfe-market-note">${esc(indicator.role)}. Latest ${esc(indicator.latestDisplay)}${indicator.latestDate ? ` on ${esc(monthLabel(indicator.latestDate))}` : ""}, ${esc(directionText(indicator.direction))}.${esc(percentile)}</p>
    <p class="sfe-market-note">${esc(indicator.note)}</p>
    <div class="sfe-market-legend">${legendMark("sfe-market-swatch-recession", "NBER recession", `${indicator.id}-recession`, NBER_LEGEND)}${zoneLegend}${legendMark("sfe-market-swatch-line", indicator.name, `${indicator.id}-line`, `The orange line is ${indicator.name}. ${indicator.note}`)}</div>
    ${renderChart(indicator, recessions, domain)}
    ${indicator.correlationText ? `<p class="sfe-market-note">${esc(indicator.correlationText)}</p>` : ""}
  </section>`;
}

let marketCards: MarketRiskSnapshot | null = null;

function cardHistoryIndicator(key: string): MarketRiskIndicator | null {
  const snapshot = marketCards;
  if (!snapshot || !key) return null;
  if (key === "risk") {
    const history = snapshot.blend?.risk?.history ?? [];
    const latest = snapshot.blend?.risk?.latest;
    if (history.length < 2) return null;
    return {
      id: "risk",
      name: "Risk",
      seriesId: null,
      role: "Recession blend",
      note: "Historical chance a recession is already underway or starts within 12 months, read from the blend.",
      latestDisplay: latest == null ? "—" : `${latest.toFixed(1)}%`,
      latestDate: history[history.length - 1]?.date ?? null,
      percentile20y: null,
      direction: "flat",
      status: riskStatus(latest),
      correlationText: null,
      history,
      chart: { zeroLine: false, threshold: 30, thresholdLabel: "Watch", valueDecimals: 1 },
    };
  }
  if (key.startsWith("indicator:")) {
    return snapshot.indicators.find((row) => row.id === key.slice("indicator:".length)) ?? null;
  }
  if (key.startsWith("shock:")) {
    const shock = snapshot.shocks.find((row) => row.id === key.slice("shock:".length));
    const history = shock?.history ?? [];
    if (!shock || history.length < 2) return null;
    return {
      id: shock.id,
      name: shock.name,
      seriesId: null,
      role: "Shock watch",
      note: shock.detail,
      latestDisplay: shock.reading ?? "—",
      latestDate: history[history.length - 1]?.date ?? null,
      percentile20y: null,
      direction: "flat",
      status: shock.status,
      correlationText: null,
      history,
      chart: { zeroLine: shock.id === "unwind" || shock.id === "funding" || shock.id === "credit", threshold: null, thresholdLabel: null, valueDecimals: 2 },
    };
  }
  return null;
}

function openCardHistory(key: string): void {
  const indicator = cardHistoryIndicator(key);
  const snapshot = marketCards;
  if (!indicator || !snapshot) return;
  const host = document.createElement("div");
  host.innerHTML = `<section class="sfe-market-chart"><h3>${esc(indicator.name)}</h3><div class="sfe-market-plot">${renderChart(indicator, snapshot.recessions, null)}</div></section>`;
  const chart = host.querySelector(".sfe-market-chart");
  if (!chart?.querySelector("svg[data-probe]")) return;
  closeShockModal();
  openChartPop(chart, { indicator, recessions: snapshot.recessions });
}

export function renderMarketRiskHtml(snapshot: MarketRiskSnapshot): string {
  const score = snapshot.drawdown.score;
  const band = snapshot.drawdown.band;
  const week =
    snapshot.drawdown.weekChange == null
      ? ""
      : snapshot.drawdown.weekChange === 0
        ? "Unchanged this week."
        : `${snapshot.drawdown.weekChange > 0 ? "Up" : "Down"} ${Math.abs(snapshot.drawdown.weekChange)} this week.`;
  const sahm =
    snapshot.recession.sahm == null
      ? "Sahm gap unavailable."
      : `Sahm gap ${snapshot.recession.sahm.toFixed(2)} pp${snapshot.recession.sahmTriggered ? ", triggered" : ", not triggered"}.`;
  const spread =
    snapshot.recession.spreadPct == null
      ? ""
      : ` Curve input ${snapshot.recession.spreadPct.toFixed(2)} pp${snapshot.recession.spreadMonth ? ` (${monthLabel(snapshot.recession.spreadMonth)} average)` : ""}.`;
  const shockById = new Map(snapshot.shocks.map((shock) => [shock.id, shock]));
  marketCards = snapshot;
  const indicatorTiles = [
    renderRiskTile(snapshot.blend),
    ...snapshot.indicators.map((indicator) => renderIndicatorTile(indicator)),
    ...EXTRA_INDICATORS.map(([series, role, note, shockId]) =>
      renderExtraTile(series, role, note, shockId ? shockById.get(shockId) : undefined),
    ),
  ].join("");
  const updated = snapshot.stale ? " Last refresh failed, so this copy may be a day old." : "";
  const chartDomain = sharedChartDomain(snapshot.indicators);
  bindShockInfo();
  bindChartProbe();
  bindChartPop();
  return `<div class="sfe-market">
    <header class="sfe-market-head">
      <h2>Market</h2>
      <p class="sfe-market-asof">As of ${esc(snapshot.asOf)}.${esc(updated)}</p>
    </header>
    <p class="sfe-market-sentence">${esc(snapshot.sentence)}</p>
    <div class="sfe-market-scores">
      <section class="sfe-market-score">
        <p class="sfe-market-kicker">Stress, next 4–8 weeks</p>
        <p class="sfe-market-value">${score == null ? "—" : esc(String(score))}${score == null ? "" : `<span class="sfe-market-value-unit"> / 100</span>`}</p>
        <p class="sfe-market-band sfe-market-band-${esc(band)}">${esc(snapshot.drawdown.label)}${score == null ? "" : ` · ${esc(stressGapLine(score))}`}</p>
        <div class="sfe-market-bar sfe-market-band-${esc(band)}" aria-hidden="true"><span style="width:${score == null ? 0 : Math.max(0, Math.min(100, score))}%"></span><i class="sfe-market-bar-mark" style="left:35%"></i><i class="sfe-market-bar-mark sfe-market-bar-mark-cut" style="left:65%"></i></div>
        <p class="sfe-market-note">${esc(week)} Not a recession chance. That is the 12-month percent. This score is calm under 35, tighten from 35, and cut margin at 65. ${esc(snapshot.drawdown.weightsNote)}</p>
      </section>
      <section class="sfe-market-score">
        <p class="sfe-market-kicker">Recession chances</p>
        <p class="sfe-market-value">${snapshot.recession.probabilityPct == null ? "—" : `${esc(snapshot.recession.probabilityPct.toFixed(1))}%`}</p>
        <p class="sfe-market-band">NY Fed curve model</p>
        <p class="sfe-market-note">${esc(sahm)}${esc(spread)}</p>
      </section>
    </div>
    <p class="sfe-market-posture">${esc(postureText(snapshot))}</p>
    <section class="sfe-market-shocks">
      <h3>Shock watch</h3>
      <p class="sfe-market-note">Double-click a card for its history. Zoom in to the week, then scroll.</p>
      <div class="sfe-market-shock-grid" style="--shock-count:${snapshot.shocks.length}">${snapshot.shocks.map((shock) => renderShock(shock)).join("")}</div>
    </section>
    <section class="sfe-market-shocks">
      <h3>Indicators used</h3>
      <p class="sfe-market-note">Safe is clear of the problem line. Tight is more than halfway there. Watch is on it. Double-click a card for its history.</p>
      <div class="sfe-market-indicator-grid">${indicatorTiles}</div>
    </section>
    <section class="sfe-market-charts">
      <h3>History through recessions</h3>
      <p class="sfe-market-note">Right-click a chart to open it larger. Use zoom, or full screen, to fill the display.</p>
      ${snapshot.blend ? renderBlend(snapshot.blend, snapshot.recessions, snapshot.indicators) : ""}
      <p class="sfe-market-note">Averages compare the 12 months before an NBER recession, the recession itself, and the other months in that chart.</p>
      ${snapshot.indicators.filter((indicator) => indicator.id === "stress").map((indicator) => renderIndicator(indicator, snapshot.recessions, chartDomain)).join("")}
      ${CHART_GROUPS.map((group) => renderRankedGroup(group, snapshot.indicators, snapshot.recessions, chartDomain)).join("")}
    </section>
    ${snapshot.footnotes.map((note) => `<p class="sfe-market-foot">${esc(note)}</p>`).join("")}
    <div class="sfe-market-modal" hidden>
      <div class="sfe-market-modal-card" role="dialog" aria-modal="true" aria-labelledby="sfe-market-modal-title">
        <div class="sfe-market-modal-head"><h3 id="sfe-market-modal-title" class="sfe-market-modal-title"></h3><button type="button" class="sfe-market-modal-close" aria-label="Close">×</button></div>
        <div class="sfe-market-modal-body"></div>
      </div>
    </div>
    <div class="sfe-market-chart-pop" hidden>
      <div class="sfe-market-chart-pop-card" role="dialog" aria-modal="true" aria-labelledby="sfe-market-chart-pop-title">
        <div class="sfe-market-chart-pop-head">
          <h3 id="sfe-market-chart-pop-title" class="sfe-market-chart-pop-title"></h3>
          <div class="sfe-market-range sfe-market-chart-pop-range" role="group" aria-label="History window" hidden><button type="button" data-range="all" aria-pressed="true">All</button><button type="button" data-range="5y" aria-pressed="false">5 years</button><button type="button" data-range="1y" aria-pressed="false">1 year</button><button type="button" data-range="1q" aria-pressed="false">1 quarter</button></div>
          <div class="sfe-market-chart-pop-tools">
            <button type="button" data-chart-zoom="out" aria-label="Zoom out">−</button>
            <span class="sfe-market-chart-pop-zoom">100%</span>
            <button type="button" data-chart-zoom="in" aria-label="Zoom in">+</button>
            <button type="button" data-chart-zoom="fit">Fit</button>
            <button type="button" data-chart-zoom="screen">Full screen</button>
            <button type="button" class="sfe-market-chart-pop-close" aria-label="Close">×</button>
          </div>
        </div>
        <div class="sfe-market-chart-pop-legend"></div>
        <div class="sfe-market-chart-pop-stage"></div>
      </div>
    </div>
  </div>`;
}

export async function fetchMarketRisk(): Promise<MarketRiskSnapshot> {
  const { baseUrl } = getApiConfig();
  const base = baseUrl ? baseUrl.replace(/\/$/, "") : "";
  const res = await proxyFetch(`${base}/market-risk`, {
    method: "GET",
    headers: { Accept: "application/json" },
  });
  if (!res.ok) throw new Error("Could not load market gauges.");
  const data = (await res.json()) as MarketRiskSnapshot;
  if (!data || data.ok !== true || !Array.isArray(data.indicators)) {
    throw new Error("Could not load market gauges.");
  }
  return data;
}

export class MarketRiskController {
  private panel: HTMLElement | null = null;

  mount(panel: HTMLElement): void {
    this.panel = panel;
    panel.innerHTML = `<div class="sfe-market"><p class="sfe-market-status">Loading market…</p></div>`;
  }

  async refresh(): Promise<void> {
    if (!this.panel) return;
    try {
      const snapshot = await fetchMarketRisk();
      if (!this.panel) return;
      this.panel.innerHTML = renderMarketRiskHtml(snapshot);
    } catch {
      if (!this.panel) return;
      this.panel.innerHTML = `<div class="sfe-market"><p class="sfe-market-status">Market gauges are unavailable.</p></div>`;
    }
  }
}
